// src/config/public-config.ts
import { z } from "zod";

import { SUPPORTED_LOCALES } from "./constants";
import "./public-config.types";

const PublicConfigSchema = z.object({
  API_BASE_URL: z.string().url(),
  SENTRY_DSN: z.string().default(""),
  SENTRY_ENVIRONMENT: z.enum(["local", "staging", "production"]).default("local"),
  RELEASE: z.string().default("dev"),
  DEFAULT_LOCALE: z.enum(SUPPORTED_LOCALES).default("en"),
  PSEUDO_LOCALE: z.string().optional(),
  // "true" enables the Manual review queue page (pending ops approval).
  FEATURE_MANUAL_REVIEW: z.string().default(""),
  // "true" enables the Revenue report (sino-legacy has none, so off by default).
  FEATURE_REVENUE_REPORT: z.string().default(""),
  // "false" shows the promotion Simulate panel; defaults to hidden (agreed
  // with Ariel) until the true dry-run rewrite ships — see
  // docs/superpowers/specs/2026-07-13-promotion-simulate-hide-flag-design.md
  // and docs/superpowers/specs/2026-07-13-promotion-simulate-dry-run-design.md.
  FEATURE_HIDE_PROMOTION_SIMULATE: z.string().default("true"),
  // "false" shows the Returns page; shown by default; set
  // PUBLIC_FEATURE_HIDE_RETURNS=true to hide.
  FEATURE_HIDE_RETURNS: z.string().default("false"),
  // "true" shows the seller-side Settlement card on order detail. Off until
  // Product reviews it (BE spec 2026-09-17-order-money-buyer-seller-breakdown §6).
  FEATURE_SETTLEMENT_PANEL: z.string().default(""),
});

export type PublicConfig = z.infer<typeof PublicConfigSchema>;

export function parsePublicConfig(raw: Record<string, unknown>): PublicConfig {
  const result = PublicConfigSchema.safeParse(raw);
  if (!result.success) {
    const issues = result.error.issues
      .map((issue) => `${issue.path.join("")}: ${issue.message}`)
      .join("; ");
    throw new Error(`Invalid public configuration: ${issues}`);
  }
  return result.data;
}

// Map Vite-exposed PUBLIC_* env vars to app-level config keys.
// Only used for local `pnpm dev` and tests; production reads runtime config.
function mapViteEnvToConfig(env: Record<string, unknown>): Record<string, unknown> {
  return {
    API_BASE_URL: env.PUBLIC_API_BASE_URL,
    SENTRY_DSN: env.PUBLIC_SENTRY_DSN,
    SENTRY_ENVIRONMENT: env.PUBLIC_SENTRY_ENVIRONMENT,
    RELEASE: env.PUBLIC_RELEASE,
    DEFAULT_LOCALE: env.PUBLIC_DEFAULT_LOCALE,
    PSEUDO_LOCALE: env.PUBLIC_PSEUDO_LOCALE,
    FEATURE_MANUAL_REVIEW: env.PUBLIC_FEATURE_MANUAL_REVIEW,
    FEATURE_REVENUE_REPORT: env.PUBLIC_FEATURE_REVENUE_REPORT,
    FEATURE_HIDE_PROMOTION_SIMULATE: env.PUBLIC_FEATURE_HIDE_PROMOTION_SIMULATE,
    FEATURE_HIDE_RETURNS: env.PUBLIC_FEATURE_HIDE_RETURNS,
    FEATURE_SETTLEMENT_PANEL: env.PUBLIC_FEATURE_SETTLEMENT_PANEL,
  };
}

function readRawConfig(): Record<string, unknown> {
  if (typeof window !== "undefined" && window.__PUBLIC_CONFIG__) {
    return window.__PUBLIC_CONFIG__ as Record<string, unknown>;
  }
  const viteEnv = mapViteEnvToConfig(import.meta.env as unknown as Record<string, unknown>);

  // In Node.js test environments (Vitest), import.meta.env may not contain
  // Vite-processed env vars. Fall back to process.env so tests that set
  // PUBLIC_* variables before importing this module work correctly.
  if (typeof process !== "undefined" && process.env) {
    if (!viteEnv.API_BASE_URL && process.env.PUBLIC_API_BASE_URL) {
      return mapViteEnvToConfig(process.env as unknown as Record<string, unknown>);
    }
  }
  return viteEnv;
}

let _cached: PublicConfig | undefined;

export function getPublicConfig(): PublicConfig {
  if (!_cached) {
    _cached = parsePublicConfig(readRawConfig());
  }
  return _cached;
}

/**
 * Browser-public runtime configuration.
 *
 * On first access, reads `window.__PUBLIC_CONFIG__` (injected by
 * `/config.js` in Docker) or falls back to Vite-exposed `PUBLIC_*`
 * env vars during local development.
 *
 * Throws if the required `API_BASE_URL` is missing or invalid.
 */
export const publicConfig = new Proxy({} as PublicConfig, {
  get(_target, prop) {
    return getPublicConfig()[prop as keyof PublicConfig];
  },
});
